import { useState, useEffect } from "react";

/**
 * Returns loading state with delayed visibility for skeleton loaders.
 * The skeleton is always rendered (to prevent layout shift) but starts transparent,
 * becoming visible only after the delay to avoid a "flash" on fast loads.
 *
 * @param isLoading - The actual loading state from your data fetching
 * @param delay - The delay in milliseconds before showing the loading state (default: 250ms)
 * @returns { shouldRender, isVisible } - shouldRender: whether to render the skeleton, isVisible: whether it should be visible
 *
 * @example
 * const { isLoading } = useQuery({ ... });
 * const { shouldRender, isVisible } = useDelayedLoading(isLoading, 500);
 *
 * // Render skeleton with opacity based on isVisible:
 * {shouldRender && <Skeleton className={isVisible ? "opacity-100" : "opacity-0"} />}
 */
export function useDelayedLoading(
  isLoading: boolean,
  delay = 250
): { shouldRender: boolean; isVisible: boolean } {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (isLoading) {
      const timer = setTimeout(() => setIsVisible(true), delay);
      return () => clearTimeout(timer);
    } else {
      setIsVisible(false);
    }
  }, [isLoading, delay]);

  return { shouldRender: isLoading, isVisible };
}
